import React, { useState, useEffect, useRef } from 'react';
import HTMLFlipBook from 'react-pageflip';
import * as pdfjsLib from 'pdfjs-dist';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import './book-reader.css';

// PDF.js worker setup
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

// Reusable Page component
const Page = React.forwardRef(({ pageNumber, pdf }, ref) => {
  const canvasRef = useRef();
  const renderTaskRef = useRef(null);

  useEffect(() => {
    let isCancelled = false;

    if (pdf) {
      pdf.getPage(pageNumber).then(page => {
        if (isCancelled) {
          return;
        }

        const canvas = canvasRef.current;
        if (!canvas) {
          return; // Canvas not available yet
        }
        
        // Set a higher resolution for the canvas to make it look sharp
        const desiredWidth = 1000; // Render at a high resolution
        const viewport = page.getViewport({ scale: 1 });
        const scale = desiredWidth / viewport.width;
        const scaledViewport = page.getViewport({ scale });
        
        const context = canvas.getContext('2d');
        
        canvas.height = scaledViewport.height;
        canvas.width = scaledViewport.width;

        const renderContext = {
          canvasContext: context,
          viewport: scaledViewport,
        };
        
        const task = page.render(renderContext);
        renderTaskRef.current = task;

        task.promise.catch(err => {
          if (err.name !== 'RenderingCancelledException') {
            console.error(`Page ${pageNumber} render error:`, err);
          }
        });
      });
    }

    return () => {
      isCancelled = true;
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
      }
    };
  }, [pdf, pageNumber]);

  return (
    <div ref={ref} className="demoPage">
      <canvas ref={canvasRef} />
    </div>
  );
});

const BookReader = ({ pdfUrl, onClose }) => {
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pdf, setPdf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const { t } = useTranslation();

  const flipBookRef = useRef();
  const containerRef = useRef();
  
  // Effect to calculate book size based on container
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        const containerHeight = containerRef.current.clientHeight;
        
        const padding = 50; // Add some padding around the book

        // For a two-page spread (2W x H, where W=H for square pages)
        const availableWidth = containerWidth - padding;
        const availableHeight = containerHeight - padding;

        const pageWidth = availableWidth / 2;
        const pageHeight = availableHeight;

        // To maintain a square aspect ratio, take the smaller of the two dimensions
        const finalSize = Math.min(pageWidth, pageHeight);
        
        setSize({ width: finalSize, height: finalSize });
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);
  
  useEffect(() => {
    const loadPdf = async () => {
      try {
        setLoading(true);
        const loadingTask = pdfjsLib.getDocument(pdfUrl);
        const pdfDoc = await loadingTask.promise;
        setPdf(pdfDoc);
        setTotalPages(pdfDoc.numPages);
      } catch (e) {
        console.error('Error loading PDF:', e);
        setError('Failed to load story. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    loadPdf();
  }, [pdfUrl]);

  const onPage = (e) => {
    setCurrentPage(e.data);
  };
  
  const handleNext = () => {
    flipBookRef.current?.pageFlip().flipNext();
  };

  const handlePrev = () => {
    flipBookRef.current?.pageFlip().flipPrev();
  };

  const renderContent = () => {
    if (loading || size.width === 0) {
      return (
        <div className="book-reader-loading">
          <div className="spinner"></div>
          <p>{t('bookReader.loading')}</p>
        </div>
      );
    }
    
    if (error) {
      return (
        <div className="book-reader-error">
          <p>{t('bookReader.error')}</p>
          <button onClick={onClose} className="btn-close">{t('bookReader.close')}</button>
        </div>
      );
    }

    return (
      <HTMLFlipBook
        width={size.width}
        height={size.height}
        size="stretch"
        maxShadowOpacity={0.5}
        showCover={true}
        mobileScrollSupport={true}
        onFlip={onPage}
        className="storybook-flipbook"
        ref={flipBookRef}
      >
        {Array.from(new Array(totalPages), (el, index) => (
          <Page
            key={`page_${index + 1}`}
            pageNumber={index + 1}
            pdf={pdf}
          />
        ))}
      </HTMLFlipBook>
    );
  };

  return (
    <div className="book-reader-overlay">
      <div className="book-reader-container" ref={containerRef}>
        {renderContent()}
      </div>
      
      {!loading && !error && (
        <div className="book-reader-controls">
          <button onClick={handlePrev} className="nav-btn" disabled={currentPage === 0}>
            <FaChevronLeft /> {t('bookReader.prev')}
          </button>
          <div className="page-indicator">
            {t('bookReader.page', { current: currentPage + 1, total: totalPages })}
          </div>
          <button onClick={handleNext} className="nav-btn" disabled={currentPage >= totalPages - 1}>
            {t('bookReader.next')} <FaChevronRight />
          </button>
        </div>
      )}

       <button onClick={onClose} className="btn-close-reader" title={t('bookReader.close')}>
         ×
       </button>
    </div>
  );
};

export default BookReader; 